/**
 * useImportSessionsList Hook
 *
 * Business logic for the patient import sessions list view.
 * Manages dual active/complete queries, tab state, session selection,
 * and delete actions.
 */

import { useCallback, useEffect, useState } from 'react';
import { useEventTracking } from '@services/statsig';

import {
  useDeleteImportSessionMutation,
  useListImportSessionsQuery,
} from '../store/hooks';
import type { SessionSummary } from '../types';

type TabValue = 'active' | 'complete';

export interface UseImportSessionsListReturn {
  activeTab: TabValue;
  setActiveTab: (tab: TabValue) => void;
  sessions: SessionSummary[];
  activeCount: number;
  completeCount: number;
  isLoading: boolean;
  hasSessions: boolean | undefined;
  selectedSessionId: string | null;
  selectSession: (id: string) => void;
  deleteSession: (id: string) => Promise<void>;
  isDeleting: boolean;
}

const ACTIVE_POLL_INTERVAL = 30_000;

export function useImportSessionsList(): UseImportSessionsListReturn {
  const { patientImport } = useEventTracking();
  const [activeTab, setActiveTabState] = useState<TabValue>('active');
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null,
  );

  // Dual queries — only poll active sessions
  const activeQuery = useListImportSessionsQuery(
    { statusFilter: 'active' },
    { pollingInterval: ACTIVE_POLL_INTERVAL, skipPollingIfUnfocused: true, refetchOnFocus: false },
  );
  const completeQuery = useListImportSessionsQuery({
    statusFilter: 'complete',
  });

  const activeSessions = activeQuery.data?.sessions ?? [];
  const completeSessions = completeQuery.data?.sessions ?? [];
  const activeCount = activeSessions.length;
  const completeCount = completeSessions.length;

  const sessions = activeTab === 'active' ? activeSessions : completeSessions;
  const isLoading =
    (activeTab === 'active' && activeQuery.isLoading) ||
    (activeTab === 'complete' && completeQuery.isLoading);

  // hasSessions: undefined while both queries are loading
  const hasSessions =
    activeQuery.isLoading && completeQuery.isLoading
      ? undefined
      : activeCount + completeCount > 0;

  // Auto-select first session when data loads or tab changes
  useEffect(() => {
    if (sessions.length > 0 && !sessions.some((s) => s.session_id === selectedSessionId)) {
      setSelectedSessionId(sessions[0].session_id);
    }
  }, [sessions, selectedSessionId]);

  const setActiveTab = useCallback((tab: TabValue) => {
    setActiveTabState(tab);
    setSelectedSessionId(null);
  }, []);

  const selectSession = useCallback((id: string) => {
    setSelectedSessionId(id);
  }, []);

  // Delete
  const [deleteImportSession, { isLoading: isDeleting }] =
    useDeleteImportSessionMutation();

  const deleteSession = useCallback(
    async (id: string) => {
      await deleteImportSession({ sessionId: id }).unwrap();
      patientImport.sessionDeleted({ session_id: id });
      if (selectedSessionId === id) {
        setSelectedSessionId(null);
      }
    },
    [deleteImportSession, selectedSessionId, patientImport],
  );

  return {
    activeTab,
    setActiveTab,
    sessions,
    activeCount,
    completeCount,
    isLoading,
    hasSessions,
    selectedSessionId,
    selectSession,
    deleteSession,
    isDeleting,
  };
}
